import cx from 'clsx';
import type { ReactElement } from 'react';
import {
  Checkbox as AriaCheckbox,
  type CheckboxProps as AriaCheckboxProps,
} from 'react-aria-components';
import { CheckmarkOutlinedCheckboxIcon } from '#components/checkbox/icons/checkmark-outlined-checkbox';
import { MinusOutlinedCheckboxIcon } from '#components/checkbox/icons/minus-outlined-checkbox';
import { SkeletonLoader } from '#components/skeleton-loader/skeleton-loader';
import styles from './selector-checkbox.module.css';

export interface SelectorCheckboxProps extends AriaCheckboxProps {
  icon?: ReactElement;
  title?: string | ReactElement;
  subtitle?: string | ReactElement;
  additionalContent?: ReactElement;
  checkboxClassName?: string;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
}

export function SelectorCheckbox({
  isDisabled = false,
  isReadOnly = false,
  isLoading = false,
  className,
  icon,
  title,
  subtitle,
  additionalContent,
  checkboxClassName,
  ...props
}: SelectorCheckboxProps): ReactElement {
  return (
    <AriaCheckbox
      aria-label={isLoading && typeof title === 'string' ? title : undefined}
      className={cx(
        styles.selector,
        styles.classic,
        isReadOnly && styles.readonly,
        isDisabled && styles.disabled,
        isLoading && styles.loading,
        className
      )}
      data-testid="selector-checkbox"
      isDisabled={isDisabled || isReadOnly || isLoading}
      isReadOnly={isReadOnly}
      {...props}
    >
      {({ isSelected, isIndeterminate }) => {
        if (isLoading) {
          return (
            <>
              <div className={styles.contentWrapper}>
                {icon ? (
                  <SkeletonLoader.Avatar
                    className={styles.icon}
                    data-testid="selector-icon-loading"
                  />
                ) : null}
                <div className={styles.header}>
                  <SkeletonLoader.Line
                    className="mb-8"
                    data-testid="selector-line-loading"
                    width="90px"
                  />
                  <SkeletonLoader.Line
                    className="mb-8"
                    data-testid="selector-line-loading"
                    width="156px"
                  />
                </div>
              </div>
              <div className={cx(styles.checkbox, checkboxClassName)} data-testid="checkbox" />
            </>
          );
        }

        return (
          <>
            <div className={styles.contentWrapper}>
              {icon ? (
                <div className={styles.icon} data-testid="selector-icon">
                  {icon}
                </div>
              ) : null}
              <div className={styles.header}>
                {title ? (
                  <span className={cx('body-standard', styles.title)} data-testid="selector-title">
                    {title}
                  </span>
                ) : null}
                {subtitle ? (
                  <span
                    className={cx('body-standard', styles.subtitle)}
                    data-testid="selector-subtitle"
                  >
                    {subtitle}
                  </span>
                ) : null}
              </div>
              {additionalContent ? (
                <div
                  className={cx('mr-8', styles.additionalContent)}
                  data-testid="selector-additional-content"
                >
                  {additionalContent}
                </div>
              ) : null}
            </div>
            <div className={cx(styles.checkbox, checkboxClassName)} data-testid="checkbox">
              {isSelected ? <CheckmarkOutlinedCheckboxIcon /> : null}
              {isIndeterminate ? <MinusOutlinedCheckboxIcon /> : null}
            </div>
          </>
        );
      }}
    </AriaCheckbox>
  );
}
